/**
 * CommunityAchievementsPage — premium achievements view.
 *
 * Pulls from /api/community/achievements/{userId}. Each achievement now
 * carries a category, rarity tier, progress fraction, and current value
 * so we can render real progress bars on locked items.
 */

import { useState, useEffect, useRef } from 'react';
import {
  Award, CheckCircle, Lock, Loader2, Trophy, Sparkles, MessageSquare,
  TrendingUp, Users, Star,
} from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { getCommunityProfile } from '../services/api';

const TIER_STYLES = {
  bronze:   { label: 'Bronze',   ring: 'border-amber-700/40',  text: 'text-amber-600',  chip: 'bg-amber-900/30 text-amber-500 border-amber-700/40',     glow: 'from-amber-700/10 to-transparent' },
  silver:   { label: 'Silver',   ring: 'border-slate-400/40',  text: 'text-slate-300',  chip: 'bg-slate-500/15 text-slate-300 border-slate-400/30',     glow: 'from-slate-400/10 to-transparent' },
  gold:     { label: 'Gold',     ring: 'border-amber-400/50',  text: 'text-amber-300',  chip: 'bg-amber-500/15 text-amber-300 border-amber-400/30',     glow: 'from-amber-400/15 to-transparent' },
  platinum: { label: 'Platinum', ring: 'border-violet-400/50', text: 'text-violet-300', chip: 'bg-gradient-to-r from-violet-500/20 to-fuchsia-500/20 text-violet-200 border-violet-400/40', glow: 'from-violet-400/20 to-transparent' },
};

const CATEGORIES = [
  { key: 'all',       label: 'All',       icon: Trophy },
  { key: 'chat',      label: 'Chat',      icon: MessageSquare },
  { key: 'trading',   label: 'Trading',   icon: TrendingUp },
  { key: 'community', label: 'Community', icon: Users },
  { key: 'special',   label: 'Special',   icon: Sparkles },
];

const METRIC_LABEL = {
  messageCount:      'messages',
  reactionsReceived: 'reactions',
  channelsCreated:   'channels',
  totalTrades:       'trades',
  chainsUsed:        'chains',
  referralCount:     'referrals',
};

export default function CommunityAchievementsPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // Independent mount-only timer — see VerifiedProjectsPage for rationale.
  const [showLoader, setShowLoader] = useState(true);
  useEffect(() => {
    const t = setTimeout(() => setShowLoader(false), 5000);
    return () => clearTimeout(t);
  }, []);
  const [activeCategory, setActiveCategory] = useState('all');

  const uid = user?.userId;

  // Race-safe fetch: tag each call and ignore stale responses. Pull from
  // the profile endpoint (instead of the separate achievements endpoint)
  // so this page is guaranteed to show the same data as the achievements
  // section inside My Profile — single source of truth.
  const latestFetchRef = useRef(0);
  useEffect(() => {
    if (!uid) return;
    const fetchId = ++latestFetchRef.current;
    const isCurrent = () => fetchId === latestFetchRef.current;

    getCommunityProfile(uid)
      .then(p => {
        if (!isCurrent() || !p) return;
        setData({ achievements: p.achievements || [], badgeDefs: {}, stats: p.stats || {} });
      })
      .catch(() => {})
      .finally(() => { if (isCurrent()) setLoading(false); });
  }, [uid]);

  // No full-page loading guard — render the shell immediately so a hung
  // request can't trap the user on a spinner.
  const achievements = data?.achievements || [];
  const earned = achievements.filter(a => a.earned);
  const locked = achievements.filter(a => !a.earned);
  const pct = achievements.length ? Math.round((earned.length / achievements.length) * 100) : 0;

  // Highest-tier earned, used for the hero title flourish
  const tierOrder = ['bronze', 'silver', 'gold', 'platinum'];
  const topTier = earned.reduce((best, a) =>
    tierOrder.indexOf(a.tier) > tierOrder.indexOf(best) ? a.tier : best, 'bronze');

  const counts = CATEGORIES.reduce((acc, c) => {
    if (c.key === 'all') acc[c.key] = achievements.length;
    else acc[c.key] = achievements.filter(a => a.category === c.key).length;
    return acc;
  }, {});

  const filtered = activeCategory === 'all'
    ? achievements
    : achievements.filter(a => a.category === activeCategory);

  // Closest-to-earning locked items, for the "Almost there" rail
  const almostThere = locked
    .filter(a => a.progress > 0 && a.progress < 1)
    .sort((a, b) => b.progress - a.progress)
    .slice(0, 3);

  return (
    <div className="space-y-6 animate-fade-in max-w-5xl">
      {/* ─── Premium Hero ──────────────────────────────────────────────── */}
      <div className="relative overflow-hidden rounded-3xl border border-dark-700/50 bg-gradient-to-br from-violet-500/10 via-dark-800/80 to-dark-900 p-6">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-4">
            <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${
              topTier === 'platinum' ? 'from-violet-500 to-fuchsia-600' :
              topTier === 'gold'     ? 'from-amber-400 to-orange-500' :
              topTier === 'silver'   ? 'from-slate-400 to-slate-500' :
                                       'from-amber-700 to-amber-800'
            } flex items-center justify-center shadow-2xl shadow-violet-500/20`}>
              <Trophy className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white flex items-center gap-2">
                Achievements
                {earned.length >= achievements.length && achievements.length > 0 && (
                  <Sparkles className="w-5 h-5 text-amber-400" />
                )}
              </h1>
              <p className="text-sm text-gray-400 mt-0.5">
                {earned.length} of {achievements.length} unlocked
                {topTier && earned.length > 0 && (
                  <span className={`ml-2 text-[10px] font-bold uppercase px-2 py-0.5 rounded border ${TIER_STYLES[topTier].chip}`}>
                    {TIER_STYLES[topTier].label} tier reached
                  </span>
                )}
              </p>
            </div>
          </div>

          {/* Big progress dial */}
          <div className="bg-dark-700/40 border border-violet-500/20 rounded-2xl px-5 py-3 min-w-[140px]">
            <p className="text-[10px] text-gray-500 uppercase tracking-wider">Completion</p>
            <p className="text-3xl font-bold bg-gradient-to-r from-violet-300 to-fuchsia-300 bg-clip-text text-transparent">{pct}%</p>
          </div>
        </div>

        {/* Progress bar */}
        <div className="mt-5">
          <div className="w-full h-2 bg-dark-700 rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-violet-500 via-fuchsia-500 to-amber-400 rounded-full transition-all"
              style={{ width: `${pct}%` }} />
          </div>
        </div>
      </div>

      {/* ─── Almost-There Rail ─────────────────────────────────────────── */}
      {almostThere.length > 0 && (
        <div className="bg-gradient-to-br from-amber-500/8 via-dark-800/80 to-dark-900 rounded-2xl border border-amber-500/20 p-5">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-4 h-4 text-amber-400" />
            <h3 className="text-xs font-bold text-amber-300 uppercase tracking-wider">Almost there</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {almostThere.map(a => {
              const t = TIER_STYLES[a.tier] || TIER_STYLES.bronze;
              const remaining = a.target - (a.current || 0);
              const metricLabel = METRIC_LABEL[a.metric] || '';
              return (
                <div key={a.id} className="bg-dark-700/40 rounded-xl p-3 border border-dark-700/50">
                  <div className="flex items-start gap-3">
                    <span className="text-2xl">{a.emoji}</span>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-white truncate">{a.name}</p>
                      <p className="text-[10px] text-gray-500">
                        {remaining} more {metricLabel}
                      </p>
                    </div>
                    <span className={`text-[9px] font-bold uppercase px-1.5 py-0.5 rounded border ${t.chip}`}>{t.label}</span>
                  </div>
                  <div className="mt-2.5">
                    <div className="w-full h-1.5 bg-dark-700 rounded-full overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-amber-400 to-orange-500 transition-all"
                        style={{ width: `${a.progress * 100}%` }} />
                    </div>
                    <p className="text-[9px] text-gray-600 mt-1">
                      {a.current || 0} / {a.target}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ─── Category Tabs ─────────────────────────────────────────────── */}
      <div className="flex gap-2 overflow-x-auto pb-1">
        {CATEGORIES.map(c => {
          const isActive = activeCategory === c.key;
          const Icon = c.icon;
          return (
            <button key={c.key} onClick={() => setActiveCategory(c.key)}
              className={`flex items-center gap-2 px-4 py-2 rounded-2xl text-sm font-semibold whitespace-nowrap transition-all border ${
                isActive
                  ? 'bg-violet-500/15 text-violet-200 border-violet-500/40'
                  : 'bg-dark-800 text-gray-400 hover:text-white border-dark-700/50 hover:border-dark-600'
              }`}>
              <Icon className="w-4 h-4" />
              {c.label}
              <span className={`text-[10px] px-1.5 py-px rounded ${
                isActive ? 'bg-violet-500/20 text-violet-300' : 'bg-dark-700 text-gray-500'
              }`}>
                {counts[c.key] || 0}
              </span>
            </button>
          );
        })}
      </div>

      {/* ─── Inline loader while initial data arrives (max 5s) ────── */}
      {showLoader && achievements.length === 0 && (
        <div className="bg-dark-800/40 rounded-2xl border border-dark-700/50 p-8 flex items-center justify-center gap-3">
          <Loader2 className="w-5 h-5 text-violet-400 animate-spin" />
          <span className="text-sm text-gray-400">Loading achievements…</span>
        </div>
      )}

      {/* ─── Earned ────────────────────────────────────────────────────── */}
      {filtered.some(a => a.earned) && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <CheckCircle className="w-4 h-4 text-emerald-400" />
            <h3 className="text-xs font-bold text-emerald-400 uppercase tracking-wider">
              Earned ({filtered.filter(a => a.earned).length})
            </h3>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {filtered.filter(a => a.earned).map(a => {
              const t = TIER_STYLES[a.tier] || TIER_STYLES.bronze;
              return (
                <div key={a.id}
                  className={`relative overflow-hidden rounded-2xl border ${t.ring} bg-gradient-to-br ${t.glow} p-4 transition-all hover:-translate-y-0.5 hover:shadow-xl group`}>
                  {/* Tier chip */}
                  <span className={`absolute top-2 right-2 text-[8px] font-bold uppercase px-1.5 py-0.5 rounded border ${t.chip}`}>
                    {t.label}
                  </span>
                  <div className="text-center">
                    <div className="text-5xl mb-2 transition-transform group-hover:scale-110 inline-block">
                      {a.emoji}
                    </div>
                    <p className="text-sm font-bold text-white">{a.name}</p>
                    <p className="text-[10px] text-gray-500 mt-1 leading-snug">{a.desc}</p>
                    <div className="mt-3 inline-flex items-center gap-1 text-[10px] font-bold text-emerald-400">
                      <CheckCircle className="w-3 h-3" /> Unlocked
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ─── Locked ────────────────────────────────────────────────────── */}
      {filtered.some(a => !a.earned) && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Lock className="w-4 h-4 text-gray-500" />
            <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider">
              Locked ({filtered.filter(a => !a.earned).length})
            </h3>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {filtered.filter(a => !a.earned).map(a => {
              const t = TIER_STYLES[a.tier] || TIER_STYLES.bronze;
              const hasProgress = a.target && a.progress > 0;
              const metricLabel = METRIC_LABEL[a.metric] || '';
              return (
                <div key={a.id}
                  className="relative overflow-hidden rounded-2xl border border-dark-700/50 bg-dark-800/40 p-4 transition-all hover:border-dark-600">
                  <span className={`absolute top-2 right-2 text-[8px] font-bold uppercase px-1.5 py-0.5 rounded border opacity-60 ${t.chip}`}>
                    {t.label}
                  </span>
                  <div className="text-center">
                    <div className="text-5xl mb-2 grayscale opacity-40 inline-block">
                      {a.emoji}
                    </div>
                    <p className="text-sm font-bold text-gray-400">{a.name}</p>
                    <p className="text-[10px] text-gray-600 mt-1 leading-snug">{a.desc}</p>
                  </div>
                  {hasProgress ? (
                    <div className="mt-3">
                      <div className="w-full h-1.5 bg-dark-700 rounded-full overflow-hidden">
                        <div className={`h-full bg-gradient-to-r ${
                          a.progress > 0.7 ? 'from-amber-400 to-orange-500' :
                          a.progress > 0.3 ? 'from-violet-500 to-fuchsia-500' :
                                              'from-slate-500 to-slate-600'
                        } transition-all`} style={{ width: `${a.progress * 100}%` }} />
                      </div>
                      <p className="text-[9px] text-gray-500 mt-1 text-center">
                        {a.current || 0} / {a.target} {metricLabel}
                      </p>
                    </div>
                  ) : (
                    <div className="mt-3 text-center">
                      <span className="inline-flex items-center gap-1 text-[10px] text-gray-600">
                        <Lock className="w-3 h-3" /> Not started
                      </span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {filtered.length === 0 && (
        <div className="bg-dark-800 rounded-2xl p-12 border border-dark-700/50 text-center">
          <Trophy className="w-10 h-10 text-gray-600 mx-auto mb-3" />
          <p className="text-gray-500 text-sm">No achievements in this category yet.</p>
        </div>
      )}

      {/* ─── Verification Badges info ──────────────────────────────────── */}
      <div className="bg-dark-800 rounded-2xl p-6 border border-dark-700/50">
        <div className="flex items-center gap-2 mb-4">
          <Star className="w-4 h-4 text-amber-400" />
          <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider">Verification Badges</h3>
        </div>
        <p className="text-xs text-gray-500 mb-4">
          Earn verification check marks displayed next to your name in chat and on your public profile.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {[
            { emoji: '🔵', label: 'Verified',         desc: 'Email confirmed + 30 days active' },
            { emoji: '🟢', label: 'Trusted Trader',   desc: '100+ trades with 60%+ win rate' },
            { emoji: '🟡', label: 'Premium Member',   desc: 'Active Elite tier subscription' },
            { emoji: '⭐', label: 'Top Contributor',  desc: 'Community voted — awarded by platform staff' },
          ].map(b => (
            <div key={b.emoji} className="flex items-center gap-3 bg-dark-700/40 rounded-xl px-4 py-3 border border-dark-700/50">
              <span className="text-2xl">{b.emoji}</span>
              <div>
                <p className="text-sm text-white font-bold">{b.label}</p>
                <p className="text-[10px] text-gray-500">{b.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
